import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { PageHeader, Card } from "../components/admin/AdminUI";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";

const DashboardAdmin = ({ setLocationAdmin }) => {
  const navigate = useNavigate();
  const [errorMe, setErrorMe] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentClass, setCurrentClass] = useState("");
  const [currentClassInput, setCurrentClassInput] = useState("");
  const [simulatedBatch, setSimulatedBatch] = useState("");
  const [simulatedBatchInput, setSimulatedBatchInput] = useState("");
  const [formStatus, setFormStatus] = useState({
    yearly: false,
    monthly: false,
    scholarship: false,
    yearlySpecial: false,
    monthlySpecial: false,
    ceaToChurch: false,
    yearlyEarly: true,
  });
  const [formStatusSaving, setFormStatusSaving] = useState("");
  const registrationForms = [
    { key: "yearly", label: "1 Year Program", path: "/registeryearly" },
    { key: "monthly", label: "1 Month Course", path: "/registermonthly" },
    { key: "scholarship", label: "Scholarship", path: "/applyscholarship" },
    { key: "yearlySpecial", label: "1 Year (Special)", path: "/registeryearlyspecial" },
    { key: "monthlySpecial", label: "1 Month (Special)", path: "/registermonthlyspecial" },
    { key: "ceaToChurch", label: "CEA To Church", path: "/ceatochurch" },
    { key: "yearlyEarly", label: "1 Year (Early Regis – Next Batch)", path: "/earlyregistration" },
  ];
  // Salin URL lengkap form (domain yang sedang dibuka + path) ke clipboard.
  const [copiedKey, setCopiedKey] = useState("");
  const handleCopyLink = async (key, path) => {
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback untuk browser/konteks tanpa Clipboard API.
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (!ok) {
        Swal.fire({ text: `Couldn't copy automatically. Link: ${url}`, icon: "info", confirmButtonColor: "#fb3104" });
        return;
      }
    }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((k) => (k === key ? "" : k)), 1500);
  };
  const classOrder = ["OT","NT","BD","BLC","BCMF","MASTERCLASS","HH","CBH","CBD","BWG","MMM","CONFERENCE"];
  const [availableBatches, setAvailableBatches] = useState(["1", "2", "3", "4"]);
  const [batchProgram, setBatchProgram] = useState("4");
  const [courseProgram, setCourseProgram] = useState("OT");
  const [dataProgram, setDataProgram] = useState();
  const [batchClassType, setBatchClassType] = useState("4");
  const [courseClassType, setCourseClassType] = useState("OT");
  const [dataClassType, setDataClassType] = useState();
  const [batchRole, setBatchRole] = useState("4");
  const [courseRole, setCourseRole] = useState("OT");
  const [dataRole, setDataRole] = useState();
  const [topDataSummary, setTopDataSummary] = useState([]);
  const [topDataBatch, setTopDataBatch] = useState("4");
  const [topDataClass, setTopDataClass] = useState("OT");
  const address = useLocation();

  const getMe = async () => {
    try {
      const response = await axios.get("/meAdmin");
    } catch (error) {
      setErrorMe(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Default every statistic class filter to the current running class. The
  // statistic dropdowns have no CONFERENCE option, so an unset/unlisted class
  // leaves the filters as they are.
  const statisticClasses = ["OT","NT","BD","BLC","BCMF","HH","MASTERCLASS","CBH","CBD","BWG","MMM"];
  const applyStatisticClass = (cls) => {
    if (!statisticClasses.includes(cls)) return;
    setCourseProgram(cls);
    setCourseClassType(cls);
    setCourseRole(cls);
    setTopDataClass(cls);
  };

  const getAndSetCurrentClass = async () => {
    try {
      const response = await axios.get("/config/currentclass");
      setCurrentClass(response.data.currentClass || "");
      setCurrentClassInput(response.data.currentClass || "");
      applyStatisticClass(response.data.currentClass || "");
    } catch (error) {}
  };

  const handleSaveCurrentClass = async () => {
    try {
      await axios.patch("/config/currentclass", { currentClass: currentClassInput });
      setCurrentClass(currentClassInput);
      applyStatisticClass(currentClassInput);
      Swal.fire({
        text: currentClassInput ? `Current class set to ${currentClassInput}` : "Current class reset",
        icon: "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
      });
    } catch (error) {
      Swal.fire({
        text: "Failed to update current class",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
      });
    }
  };

  const getAndSetSimulatedBatch = async () => {
    try {
      const response = await axios.get("/config/simulatedbatch");
      setSimulatedBatch(response.data.simulatedBatch || "");
      setSimulatedBatchInput(response.data.simulatedBatch || "");
    } catch (error) {}
  };

  const handleSaveSimulatedBatch = async (overrideValue) => {
    const value = overrideValue !== undefined ? overrideValue : simulatedBatchInput;
    try {
      await axios.patch("/config/simulatedbatch", { simulatedBatch: value });
      setSimulatedBatch(value);
      setSimulatedBatchInput(value);
      fetchAvailableBatches();
      Swal.fire({
        text: value
          ? `Current running batch set to ${value}`
          : "Current running batch reset",
        icon: "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
      });
    } catch (error) {
      Swal.fire({
        text: "Failed to update the current running batch",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
      });
    }
  };

  const getAndSetFormStatus = async () => {
    try {
      const response = await axios.get("/config/formstatus");
      setFormStatus(response.data);
    } catch (error) {}
  };

  // Tutup/buka formulir pendaftaran. Saat ditutup, tombol pendaftaran di sisi
  // user memunculkan popup waiting list dan route form-nya dialihkan balik.
  const handleToggleForm = async (form, label) => {
    const closed = !formStatus[form];
    setFormStatusSaving(form);
    try {
      await axios.patch("/config/formstatus", { form, closed });
      setFormStatus((prev) => ({ ...prev, [form]: closed }));
      // Toast supaya toggle tetap cepat dipakai — tidak perlu klik OK tiap ganti.
      Swal.fire({
        toast: true,
        position: "top-end",
        text: closed
          ? `${label} registration closed`
          : `${label} registration opened`,
        icon: "success",
        background: "white",
        color: "black",
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });
    } catch (error) {
      Swal.fire({
        text: `Failed to update ${label} registration status`,
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
      });
    } finally {
      setFormStatusSaving("");
    }
  };

  const applyStatisticBatch = (batch) => {
    setBatchProgram(batch);
    setBatchClassType(batch);
    setBatchRole(batch);
    setTopDataBatch(batch);
  };

  const fetchAvailableBatches = async () => {
    try {
      const [batchesRes, runningRes] = await Promise.all([
        axios.get("/batches"),
        axios.get("/config/simulatedbatch").catch(() => ({ data: {} })),
      ]);
      let batches = batchesRes.data.batches || [];
      const running = runningRes.data.simulatedBatch ? String(runningRes.data.simulatedBatch) : "";
      if (running && !batches.map(String).includes(running)) {
        batches = [...batches, running].sort((a, b) => Number(a) - Number(b));
      }
      if (batches.length > 0) {
        setAvailableBatches(batches);
        // Default every statistic filter to the current running batch; fall back to the latest batch.
        applyStatisticBatch(running || batches[batches.length - 1]);
      }
    } catch (error) {}
  };

  const getProgramSummary = async (e) => {
    try {
      const response = await axios.post("/programdata", {
        className: courseProgram,
        batch: batchProgram,
      });
      setDataProgram(response.data);
    } catch (error) {
      setErrorMe(true);
    } finally {
      setIsLoading(false);
    }
  };

  // console.log(dataProgram);

  useEffect(() => {
    getProgramSummary();
  }, [courseProgram, batchProgram]);

  const getClassTypeSummary = async (e) => {
    try {
      const response = await axios.post("/onsiteonlinesummary", {
        className: courseClassType,
        batch: batchClassType,
      });
      setDataClassType(response.data);
    } catch (error) {
      setErrorMe(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getClassTypeSummary();
  }, [courseClassType, batchClassType]);

  const getTopDataSummary = async (e) => {
    try {
      const response = await axios.post("/topsummarydata", {
        batchJoined: topDataBatch,
        className: topDataClass,
      });
      setTopDataSummary(response.data);
    } catch (error) {
      setErrorMe(true);
    } finally {
      setIsLoading(false);
    }
    // console.log(topDataBatch);
    // console.log(topDataClass);
  };

  // console.log(topDataSummary);

  useEffect(() => {
    getTopDataSummary();
  }, [topDataClass, topDataBatch]);

  const getRoleSummary = async (e) => {
    try {
      const response = await axios.post("/rolesummary", {
        className: courseRole,
        batch: batchRole,
      });
      setDataRole(response.data);
    } catch (error) {
      setErrorMe(true);
    } finally {
      setIsLoading(false);
    }
  };

  // console.log(dataRole);

  useEffect(() => {
    getRoleSummary();
  }, [courseRole, batchRole]);

  useEffect(() => {
    setLocationAdmin(address);
    getMe();
    getAndSetCurrentClass();
    getAndSetSimulatedBatch();
    getAndSetFormStatus();
    fetchAvailableBatches();
  }, []);

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account !",
        icon: "warning",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        showCancelButton: false,
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
        customClass: {
          confirmButton: "custom-btn-sweetalrt",
        },
      });

      navigate("/loginAdmin");
    }
  }, [errorMe, navigate]);

  const statTiles = [
    { label: "Total Students", value: topDataSummary?.totalStudents, icon: "bi-people" },
    { label: "Onsite", value: topDataSummary?.onsite, icon: "bi-building" },
    { label: "Online", value: topDataSummary?.online, icon: "bi-laptop" },
    { label: "Graduates", value: topDataSummary?.graduates, icon: "bi-mortarboard" },
  ];

  return (
    <div className="adm-main">
      <PageHeader
        title="Dashboard"
        subtitle="Overview of students, running batch, and registration forms."
      />

      {/* ── Pengaturan ── */}
      <div>
        <Card title="Academic Settings" subtitle="Used for registration forms and batch calculation.">
          <div className="adm-settings-grid">
          <div className="adm-setting-row">
            <div>
              <p className="adm-setting-label">Current Running Class</p>
              <p className="adm-setting-value">{currentClass || "Not set"}</p>
            </div>
            <div className="adm-setting-control">
              <select
                className="form-select"
                style={{ width: "150px" }}
                value={currentClassInput}
                onChange={(e) => setCurrentClassInput(e.target.value)}
              >
                <option value="">Not set</option>
                {classOrder.map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
              <button type="button" className="adm-btn adm-btn--primary" onClick={handleSaveCurrentClass}>
                Save
              </button>
            </div>
          </div>

          <div className="adm-setting-row">
            <div>
              <p className="adm-setting-label">Current Running Batch</p>
              <p className="adm-setting-value">{simulatedBatch ? `Batch ${simulatedBatch}` : "Not set"}</p>
            </div>
            <div className="adm-setting-control">
              <input
                type="text"
                className="form-control"
                style={{ width: "90px" }}
                placeholder="e.g. 5"
                value={simulatedBatchInput}
                onChange={(e) => setSimulatedBatchInput(e.target.value.replace(/\D/g, ""))}
              />
              <button type="button" className="adm-btn adm-btn--primary" onClick={() => handleSaveSimulatedBatch()}>
                Save
              </button>
              {simulatedBatch && (
                <button type="button" className="adm-btn adm-btn--secondary" onClick={() => handleSaveSimulatedBatch("")}>
                  Clear
                </button>
              )}
            </div>
          </div>
          </div>
        </Card>

        <Card className="adm-section" title="Registration Forms" subtitle="Closed forms only show the waiting-list popup.">
          <div className="adm-toggle-list">
            {registrationForms.map((f) => (
              <div key={f.key} className="adm-toggle-row">
                <div style={{ minWidth: 0 }}>
                  <p className="adm-toggle-title text-truncate">{f.label}</p>
                  <button
                    type="button"
                    className="adm-copy-link"
                    onClick={() => handleCopyLink(f.key, f.path)}
                    title={`${window.location.origin}${f.path}`}
                    aria-label={`Copy link for ${f.label}`}
                  >
                    <i className={`bi ${copiedKey === f.key ? "bi-check2" : "bi-link-45deg"}`} aria-hidden="true" />
                    {copiedKey === f.key ? "Copied!" : "Copy Link"}
                  </button>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className={`adm-badge ${formStatus[f.key] ? "adm-badge--danger" : "adm-badge--success"}`}>
                    {formStatus[f.key] ? "Closed" : "Open"}
                  </span>
                  <div className="form-check form-switch m-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      style={{ cursor: "pointer" }}
                      aria-label={`Toggle ${f.label}`}
                      checked={!formStatus[f.key]}
                      disabled={formStatusSaving === f.key}
                      onChange={() => handleToggleForm(f.key, f.label)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ── Ringkasan student ── */}
      <Card
        className="adm-section"
        title="Students Overview"
        subtitle={`Batch ${topDataBatch} · ${topDataClass === "all" ? "All classes" : topDataClass}`}
        actions={
          <>
            <select
              className="form-select form-select-sm"
              style={{ width: "130px" }}
              value={topDataBatch}
              onChange={(e) => setTopDataBatch(e.target.value)}
            >
              {availableBatches.map((b) => (
                <option key={b} value={b}>Batch {b}</option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              style={{ width: "150px" }}
              value={topDataClass}
              onChange={(e) => setTopDataClass(e.target.value)}
            >
              <option value="all">All Classes</option>
              {CHART_CLASS_OPTIONS.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </>
        }
      >
        <div className="adm-stats" style={{ marginBottom: 0 }}>
          {statTiles.map((tile) => (
            <div key={tile.label} className="adm-stat adm-stat--icon">
              <span className="adm-stat-icon">
                <i className={`bi ${tile.icon}`}></i>
              </span>
              <div>
                <p className="adm-stat-label">{tile.label}</p>
                <p className="adm-stat-value">{tile.value ?? "–"}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* ── Grafik ── */}
      <div className="adm-section">
        <ChartCard
          title="Program Summary"
          batch={batchProgram}
          onBatchChange={setBatchProgram}
          course={courseProgram}
          onCourseChange={setCourseProgram}
          batches={availableBatches}
          data={dataProgram?.data}
          bars={[
            { key: "1 Month Program", color: CHART_COLORS.month },
            { key: "1 Year Program", color: CHART_COLORS.year },
          ]}
        />
      </div>
      <div className="adm-section">
        <ChartCard
          title="Onsite / Online Summary"
          batch={batchClassType}
          onBatchChange={setBatchClassType}
          course={courseClassType}
          onCourseChange={setCourseClassType}
          batches={availableBatches}
          data={dataClassType?.data}
          bars={[
            { key: "online", label: "Online", color: CHART_COLORS.online },
            { key: "onsite", label: "Onsite", color: CHART_COLORS.onsite },
          ]}
        />
      </div>
      <div className="adm-section">
        <ChartCard
          title="Role Summary"
          batch={batchRole}
          onBatchChange={setBatchRole}
          course={courseRole}
          onCourseChange={setCourseRole}
          batches={availableBatches}
          data={dataRole?.data}
          bars={ROLE_BARS}
        />
      </div>
    </div>
  );
};

// Kelas yang bisa dipilih di filter grafik (urutan sama seperti sebelumnya).
const CHART_CLASS_OPTIONS = ["OT", "NT", "BD", "BLC", "BCMF", "HH", "MASTERCLASS", "CBH", "CBD", "BWG", "MMM"];

const CHART_COLORS = {
  month: "#fb3104",
  year: "#2563eb",
  online: "#fb3104",
  onsite: "#2563eb",
};

const ROLE_BARS = [
  { key: "hits", label: "HITS/Mth", color: "#6b7280" },
  { key: "pastoral", label: "Pastoral", color: "#fb3104" },
  { key: "staff", label: "Staff", color: "#2563eb" },
  { key: "coach", label: "Coach", color: "#16a34a" },
  { key: "cgl", label: "CGL", color: "#a855f7" },
  { key: "ctl", label: "CTL", color: "#5d3a9b" },
  { key: "attendees", label: "Attendees", color: "#b45309" },
  { key: "other attendees", label: "Other Attendees", color: "#0d9488" },
];

// Satu kartu grafik batang + filter batch/kelas. Kalau batch "all", sumbu X
// per batch; selain itu per kelas — sama seperti sebelumnya.
const ChartCard = ({ title, batch, onBatchChange, course, onCourseChange, batches, data, bars }) => (
  <Card
    title={title}
    actions={
      <>
        <select
          className="form-select form-select-sm"
          style={{ width: "130px" }}
          value={batch}
          onChange={(e) => onBatchChange(e.target.value)}
        >
          {batches.map((b) => (
            <option key={b} value={b}>Batch {b}</option>
          ))}
          <option value="all">All Batch</option>
        </select>
        <select
          className="form-select form-select-sm"
          style={{ width: "150px" }}
          value={course}
          onChange={(e) => onCourseChange(e.target.value)}
        >
          <option value="all">All Classes</option>
          {CHART_CLASS_OPTIONS.map((code) => (
            <option key={code} value={code}>{code}</option>
          ))}
        </select>
      </>
    }
  >
    <div className="adm-chart-legend">
      {bars.map((bar) => (
        <span key={bar.key} className="adm-chart-legend-item">
          <span className="adm-chart-legend-dot" style={{ backgroundColor: bar.color }} />
          {bar.label || bar.key}
        </span>
      ))}
    </div>
    <div style={{ width: "100%", height: 320 }}>
      <ResponsiveContainer>
        <BarChart data={data || []} margin={{ top: 24, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
          <XAxis dataKey={batch === "all" ? "batch" : "className"} tick={{ fontSize: 12 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
          <Tooltip cursor={{ fill: "rgba(0,0,0,0.03)" }} />
          {bars.map((bar) => (
            <Bar key={bar.key} dataKey={bar.key} name={bar.label || bar.key} fill={bar.color} radius={[4, 4, 0, 0]}>
              <LabelList dataKey={bar.key} position="top" style={{ fontSize: 11, fill: "#4b5563" }} />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

export default DashboardAdmin;
